import { AnimatePresence, motion } from "motion/react";
import { useMemo } from "react";
import { panelSpring } from "../lib/motion";
import { usePresentedSnapshot } from "../presentation/hooks";
import { useNexusStore } from "../store/nexusStore";

function coord(value: number, axis: string) {
  return `${axis} ${value >= 0 ? "+" : "−"}${Math.abs(value).toFixed(2).padStart(5, "0")}`;
}

export function InstrumentReadout() {
  const hovered = useNexusStore((state) => state.hoveredEntityId);
  const snapshot = usePresentedSnapshot();
  const model = useMemo(() => {
    if (!hovered || !snapshot) return null;
    const node = snapshot.nodes.find((item) => item.id === hovered);
    if (node)
      return {
        id: node.id,
        label: node.label,
        eyebrow: node.eyebrow,
        status: node.status,
        kind: node.kind,
        position: node.position,
        meta: node.metrics?.meta ?? node.cluster,
      };
    const orbital = snapshot.orbitalNodes.find((item) => item.id === hovered);
    if (!orbital) return null;
    const parent = snapshot.nodes.find((item) => item.id === orbital.parentId);
    return {
      id: orbital.id,
      label: orbital.label,
      eyebrow: orbital.eyebrow,
      status: orbital.status,
      kind: orbital.kind,
      position: parent?.position ?? ([0, 0, 0] as [number, number, number]),
      meta: orbital.metrics?.meta ?? orbital.cluster,
    };
  }, [hovered, snapshot]);

  return (
    <AnimatePresence mode="wait">
      {model && (
        <motion.aside
          key={model.id}
          className="instrument-readout"
          aria-hidden="true"
          initial={{ opacity: 0, x: -10, scale: 0.985, filter: "blur(5px)" }}
          animate={{ opacity: 1, x: 0, scale: 1, filter: "blur(0px)" }}
          exit={{ opacity: 0, x: -6, scale: 0.992, filter: "blur(3px)" }}
          transition={panelSpring}
        >
          <div>
            <span>OBSERVATION TARGET</span>
            <b>{model.label}</b>
            <small>{model.eyebrow}</small>
          </div>
          <dl>
            <div>
              <dt>RA</dt>
              <dd>{coord(model.position[0], "")}</dd>
            </div>
            <div>
              <dt>DEC</dt>
              <dd>{coord(model.position[1], "")}</dd>
            </div>
            <div>
              <dt>STATE</dt>
              <dd>{model.status.toUpperCase()}</dd>
            </div>
            <div>
              <dt>CLASS</dt>
              <dd>{String(model.kind).toUpperCase()}</dd>
            </div>
          </dl>
          <footer>{String(model.meta).toUpperCase()}</footer>
        </motion.aside>
      )}
    </AnimatePresence>
  );
}
